/**
 * Format ISO datetime to readable date
 * Example: "2025-11-10T06:50:00" → "Mon, Nov 10, 2025"
 */
export const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const options = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  return date.toLocaleDateString('en-US', options);
};

/**
 * Format ISO datetime to time only
 * Example: "2025-11-10T06:50:00" → "06:50"
 */
export const formatTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Format duration in minutes to human readable
 * Example: 95 → "1h 35m"
 */
export const formatDuration = (minutes) => {
  if (!minutes) return '0h 0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

/**
 * Get cabin class name from code
 * @param {number} cabinClass - 1=All, 2=Economy, 3=Premium Economy, 4=Business, 5=Premium Business, 6=First
 */
export const getCabinClassName = (cabinClass) => {
  const cabinMap = {
    1: 'All',
    2: 'Economy',
    3: 'Premium Economy',
    4: 'Business',
    5: 'Premium Business',
    6: 'First Class'
  };
  return cabinMap[cabinClass] || 'Economy';
};

/**
 * Get passenger type name from code
 * @param {number} passengerType - 1=Adult, 2=Child, 3=Infant
 */
export const getPassengerTypeName = (passengerType) => {
  const typeMap = {
    1: 'Adult',
    2: 'Child',
    3: 'Infant'
  };
  return typeMap[passengerType] || 'Adult';
};

/**
 * Calculate age from date of birth
 * @param {string} dateOfBirth - Date string in YYYY-MM-DD format
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 0;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/**
 * Calculate total flight duration including layovers
 * @param {Array} segments - Array of flight segments
 */
export const calculateTotalDuration = (segments) => {
  if (!segments || segments.length === 0) return 0;
  const lastSegment = segments[segments.length - 1];
  return lastSegment.AccumulatedDuration || 0;
};

/**
 * Parse tax breakdown for display
 * @param {Array} taxBreakup - Tax breakdown array from API
 */
export const parseTaxBreakdown = (taxBreakup) => {
  if (!taxBreakup || taxBreakup.length === 0) return [];
  return taxBreakup.map(tax => ({
    label: tax.key,
    amount: tax.value
  }));
};

/**
 * Check if flight arrives next day
 * @param {string} departureTime - Departure ISO datetime
 * @param {string} arrivalTime - Arrival ISO datetime
 */
export const isOvernightFlight = (departureTime, arrivalTime) => {
  if (!departureTime || !arrivalTime) return false;
  const depDate = new Date(departureTime);
  const arrDate = new Date(arrivalTime);
  // Check if arrival is after departure AND on a different date
  return arrDate > depDate && arrDate.getDate() !== depDate.getDate();
};

/**
 * Get day difference for overnight flights
 * @param {string} departureTime - Departure ISO datetime
 * @param {string} arrivalTime - Arrival ISO datetime
 */
export const getDayDifference = (departureTime, arrivalTime) => {
  if (!departureTime || !arrivalTime) return 0;
  const depDate = new Date(departureTime);
  const arrDate = new Date(arrivalTime);
  const diffTime = Math.abs(arrDate - depDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays - 1; // -1 because same day is 0
};

/**
 * Get City Name from Segment data with fallback mapping for known codes
 * @param {Object} airportData - The Airport object from Origin or Destination
 */
export const getCityName = (airportData) => {
  if (!airportData) return "N/A";

  // Priority 1: Direct name from API
  if (airportData.CityName) return airportData.CityName;
  if (airportData.AirportName && !airportData.AirportName.toLowerCase().includes("airport")) return airportData.AirportName;

  // Priority 2: Known Airport Codes Mapping (Fallback for API gaps)
  const airportMap = {
    HSR: "Rajkot",
    RAJ: "Rajkot",
    AMD: "Ahmedabad",
    BOM: "Mumbai",
    DEL: "Delhi",
    BLR: "Bengaluru",
    HYD: "Hyderabad",
    MAA: "Chennai",
    CCU: "Kolkata",
    PNQ: "Pune",
    GOI: "Goa",
    GOX: "Goa (Mopa)",
    ATQ: "Amritsar",
    COK: "Kochi",
    TRV: "Thiruvananthapuram",
    CCJ: "Kozhikode",
    LKO: "Lucknow",
    VNS: "Varanasi",
    IXC: "Chandigarh",
    IXB: "Bagdogra",
    JAI: "Jaipur",
    BDQ: "Vadodara",
    SUR: "Surat",
    IDR: "Indore",
    NAG: "Nagpur",
  };

  const code = airportData.CityCode || airportData.AirportCode;
  if (code && airportMap[code.toUpperCase()]) {
    return airportMap[code.toUpperCase()];
  }

  // Priority 3: Use Codes as last resort
  return code || "N/A";
};
